import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import {
  fetchDoctorsList,
  fetchUpdateDoctorLicenceStatus,
  fetchAssignOrRevokeDoctorBadges,
} from 'modules/doctors/actions';
import { doctorStatusEnum } from 'modules/doctors/enums';
import { getDoctorBadges } from 'modules/doctors/api';
import { readPreSignedUrl } from 'modules/common/api';
import DoctorsComponent from 'modules/doctors/components/DoctorsComponent';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const DoctorsContainer = ({ doctorsListApiCallBody }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const { doctorsList } = useSelector(state => ({
    doctorsList: state.doctors.doctorsList,
  }));

  const dispatch = useDispatch();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [doctorsListApiCallParams, setDoctorsListApiCallParams] = useState(doctorsListApiCallBody);
  const [showCopiedTextOverlay, setShowCopiedTextOverlay] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPageFilter, setSelectedPageFilter] = useState(1);
  const [isPreviewFilePopupOpen, setIsPreviewFilePopupOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState('');
  const [isActionPopupOpen, setIsActionPopupOpen] = useState(false);
  const [isActionPopupLoading, setIsActionPopupLoading] = useState(false);
  const [actionPopupStatus, setActionPopupStatus] = useState('');
  const [actionPopupTarget, setActionPopupTarget] = useState('');
  const [isBadgePopupOpen, setIsBadgePopupOpen] = useState(false);
  const [isBadgePopupLoading, setIsBadgePopupLoading] = useState(false);
  const [badgePopupTarget, setBadgePopupTarget] = useState('');
  const [loadedDoctorsBadges, setLoadedDoctorsBadges] = useState({});
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  const submitSearchQuery = (reset, value) => {
    setSelectedPageFilter(1);

    const body = {
      data: {
        ...doctorsListApiCallParams,
      },
    };

    if (value) body.data.searchKeyword = value;
    if (reset) delete body.data.searchKeyword;

    setDoctorsListApiCallParams(body.data);
    dispatch(fetchDoctorsList(body));
  };

  const reloadDoctorsPage = () => {
    const totalResultsCount = doctorsList?.details?.totalCount;
    const numberOfPages = (totalResultsCount && Math.ceil((totalResultsCount - 1) / 10)) || 1;

    const data = {
      ...doctorsListApiCallParams,
    };

    if (selectedPageFilter > numberOfPages) {
      const previousPage = selectedPageFilter - 1;
      setSelectedPageFilter(previousPage);
      data.Page = previousPage;
    } else data.Page = selectedPageFilter;

    const body = {
      data,
    };

    dispatch(fetchDoctorsList(body));
  };

  const actionPopupConfirmBtnCallback = shouldReloadDoctors => {
    setIsActionPopupOpen(false);
    setIsActionPopupLoading(false);
    setActionPopupStatus('');
    setActionPopupTarget('');

    if (shouldReloadDoctors) reloadDoctorsPage();
  };

  const badgePopupConfirmBtnCallback = shouldReloadDoctors => {
    setIsBadgePopupOpen(false);
    setIsBadgePopupLoading(false);
    setBadgePopupTarget('');
    setLoadedDoctorsBadges({});

    if (shouldReloadDoctors) reloadDoctorsPage();
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleDoctorStatusFilterClick = selectedStatus => {
    setSelectedStatusFilter(selectedStatus);
    setSelectedPageFilter(1);

    const body = {
      data: {
        ...doctorsListApiCallParams,
      },
    };
    if (selectedStatus) {
      body.data.LicenceStatus = doctorStatusEnum.getKeyByValue(selectedStatus);
    } else {
      delete body.data.LicenceStatus;
    }

    setDoctorsListApiCallParams(body.data);

    dispatch(fetchDoctorsList(body));
  };

  const handleSearchBarInputChange = event => {
    const { value } = event.target;
    const searchQueryMinLength = 3;

    if (value.length > 30) return;
    setSearchQuery(value);

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);

        submitSearchQuery(false, value);
      }, 300);
    }
    // this condition checks if we are deleteing letters from search query and we reached
    // 2 characters, then we need to clear search query filter
    else if (searchQuery.length >= searchQueryMinLength && value.length < searchQueryMinLength)
      submitSearchQuery(true);
  };

  const handleDoctorsPaginationClick = selectedPageNumber => {
    setSelectedPageFilter(selectedPageNumber);

    const data = {
      ...doctorsListApiCallParams,
    };
    data.Page = selectedPageNumber;
    const body = {
      data,
    };

    dispatch(fetchDoctorsList(body));
  };

  const handleTextToCopyToClipboardClick = textToCopy => {
    navigator.clipboard.writeText(textToCopy);
    setShowCopiedTextOverlay(true);

    setTimeout(() => {
      setShowCopiedTextOverlay(false);
    }, 1000);
  };

  const handlePreviewFileClick = (openPopup, fileUrl) => {
    if (openPopup) {
      const body = {
        headers: {
          ServiceKey: 'dn-licence',
          'x-vzt-Component': 'doctornetwork protal',
        },
        data: {
          FilePaths: fileUrl,
        },
      };
      readPreSignedUrl(body).then(response => {
        setPreviewFileUrl(response.data.data[0].presignedURL);
        setIsPreviewFilePopupOpen(true);
      });
    } else setIsPreviewFilePopupOpen(false);
  };

  const handleActionBtnClick = (profileKey, actionStatus) => {
    setIsActionPopupOpen(true);
    setActionPopupStatus(actionStatus);
    setActionPopupTarget(profileKey);
  };

  const handleBadgeBtnClick = profileKey => {
    setIsBadgePopupOpen(true);
    setBadgePopupTarget(profileKey);
    setIsBadgePopupLoading(true);

    const body = {
      profileKey,
    };
    getDoctorBadges(body)
      .then(response => {
        setLoadedDoctorsBadges({
          [profileKey]: response.data.data.map(badge => badge.badgeKey),
        });
        setIsBadgePopupLoading(false);
      })
      .catch(() => {
        setIsBadgePopupOpen(false);
        setBadgePopupTarget('');
      });
  };

  const handleActionPopupConfirmBtnClick = () => {
    setIsActionPopupLoading(true);

    const body = {
      data: {
        LicenceStatus: actionPopupStatus,
      },
      profileKey: actionPopupTarget,
      actionPopupCallbackAction: actionPopupConfirmBtnCallback,
    };

    dispatch(fetchUpdateDoctorLicenceStatus(body));
  };

  const handleBadgePopupConfirmBtnClick = (badgeKey, isAssign) => {
    setIsBadgePopupLoading(true);

    const body = {
      data: {
        BadgeKeys: [badgeKey],
      },
      isAssign,
      profileKey: badgePopupTarget,
      badgePopupCallbackAction: badgePopupConfirmBtnCallback,
    };

    dispatch(fetchAssignOrRevokeDoctorBadges(body));
  };

  const handleActionPopupDismissBtnClick = () => {
    setIsActionPopupOpen(false);
    setActionPopupStatus('');
    setActionPopupTarget('');
  };

  const handleBadgePopupDismissBtnClick = () => {
    setIsBadgePopupOpen(false);
    setBadgePopupTarget('');
    setLoadedDoctorsBadges({});
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <DoctorsComponent
      showCopiedTextOverlay={showCopiedTextOverlay}
      selectedStatusFilter={selectedStatusFilter}
      searchQuery={searchQuery}
      selectedPageFilter={selectedPageFilter}
      isPreviewFilePopupOpen={isPreviewFilePopupOpen}
      previewFileUrl={previewFileUrl}
      isActionPopupOpen={isActionPopupOpen}
      isActionPopupLoading={isActionPopupLoading}
      actionPopupStatus={actionPopupStatus}
      isBadgePopupOpen={isBadgePopupOpen}
      isBadgePopupLoading={isBadgePopupLoading}
      badgePopupTarget={badgePopupTarget}
      loadedDoctorsBadges={loadedDoctorsBadges}
      handleTextToCopyToClipboardClick={handleTextToCopyToClipboardClick}
      handleDoctorStatusFilterClick={handleDoctorStatusFilterClick}
      handleSearchBarInputChange={handleSearchBarInputChange}
      handleDoctorsPaginationClick={handleDoctorsPaginationClick}
      handlePreviewFileClick={handlePreviewFileClick}
      handleActionBtnClick={handleActionBtnClick}
      handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
      handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
      handleBadgeBtnClick={handleBadgeBtnClick}
      handleBadgePopupConfirmBtnClick={handleBadgePopupConfirmBtnClick}
      handleBadgePopupDismissBtnClick={handleBadgePopupDismissBtnClick}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

DoctorsContainer.propTypes = {
  doctorsListApiCallBody: PropTypes.shape({}).isRequired,
};

DoctorsContainer.defaultProps = {};

export default DoctorsContainer;
